// The marks the tap notation row draws (issue #3473): where every expected note sits, as a ghost,
// and where the singer actually tapped, as a note in the result colour, on one time axis, so a
// yellow beat shows whether the tap was early or late and by how much. Plain data from the
// scorer's PatternResult; no DOM. The same function serves the live row during the tap phase
// (taps so far plus the clock, so a miss only turns red once its band has passed) and the result
// screen (no clock: every miss is red).

import { rhythmLevelParams } from '../../../../../common/src/learn_rules.ts';
import { LearnLevel } from '../../../../../common/src/model.ts';
import {
  beatMs,
  BeatResult,
  isInBand,
  matchBandMs,
  NoteValue,
  OnsetBand,
  onsetBandsMs,
  patternOnsetBeats,
  patternOnsetsMs,
  ResultColor,
  rhythmBeatsPerBar,
  RhythmPattern,
  scorePattern,
} from '../../../../../common/src/rhythm_echo.ts';
import { resultColorForStatus } from './rhythm_blocks.tsx';
import { eventStartBeats } from './rhythm_layout.tsx';

// A ghost is 'hit' once a tap has landed in its band, 'missed' when none did and its band has
// passed (or the pattern is over), and 'pending' before that. A rest is always pending: nothing
// is expected there.
export type TapGhostStatus = 'pending' | 'hit' | 'missed';

export interface TapGhost {
  beats: number; // where the note (or rest) starts, in beats from the bar start
  value: NoteValue;
  rest: boolean;
  status: TapGhostStatus;
}

// 'matched' is the one tap in a beat's band nearest its onset, drawn with the beat's expected
// note value; 'extra' is any other tap (a stray outside every band, or a second tap in a band),
// drawn as a quarter. A matched tap is green on time and yellow early or late; every tap of a beat
// that extra taps have turned into a miss is red.
export type TapMarkKind = 'matched' | 'extra';

export interface TapMark {
  beats: number; // when the tap landed, in beats from the bar start, clamped to the bar
  tapMs: number; // the tap as scored, in ms from the tap bar start
  value: NoteValue;
  color: ResultColor;
  kind: TapMarkKind;
}

export interface TapNotationMarks {
  ghosts: TapGhost[]; // one per note and rest of the pattern, in pattern order
  taps: TapMark[]; // every tap that counted, in time order (the order of PatternResult.tapsMs)
}

// `tapsMs` are the singer's taps in ms from the tap bar start (what scorePattern takes) and
// `nowMs` the clock on the same scale while the row is live; omit it for a finished pattern.
export function tapNotationMarks({
  pattern,
  level,
  tapsMs,
  nowMs,
}: {
  pattern: RhythmPattern;
  level: LearnLevel;
  tapsMs: number[];
  nowMs?: number;
}): TapNotationMarks {
  const { bpm } = rhythmLevelParams[level];
  const msPerBeat = beatMs(bpm);
  const bandMs = matchBandMs(level);
  const bands = onsetBandsMs(patternOnsetsMs(pattern, bpm), bandMs, bpm);
  const result = scorePattern(pattern, level, tapsMs);
  const onsetValues = onsetNoteValues(pattern);
  const onsetBeats = patternOnsetBeats(pattern);
  const ghosts: TapGhost[] = [];
  const taps: TapMark[] = [];
  const starts = eventStartBeats(pattern);
  let onset = 0;
  pattern.forEach((event, eventIndex) => {
    if (event.notation.rest) {
      ghosts.push({ beats: starts[eventIndex], value: event.notation.value, rest: true, status: 'pending' });
      return;
    }
    for (const _onset of event.onsets) {
      const beatResult = result.beats[onset];
      const inBand = tapsInBand(beatResult, bands[onset]);
      const passed = nowMs === undefined || nowMs > beatResult.onsetMs + bandMs;
      ghosts.push({
        beats: onsetBeats[onset],
        value: onsetValues[onset],
        rest: false,
        status: inBand.length > 0 ? 'hit' : passed ? 'missed' : 'pending',
      });
      taps.push(...beatTapMarks(beatResult, inBand, onsetValues[onset], msPerBeat));
      onset += 1;
    }
  });
  taps.sort((lhs, rhs) => lhs.tapMs - rhs.tapMs);
  return { ghosts, taps };
}

// The note value of each onset, in onset order: the value of the event it belongs to
function onsetNoteValues(pattern: RhythmPattern): NoteValue[] {
  return pattern.flatMap((event) => event.onsets.map(() => event.notation.value));
}

// The beat's taps inside its band, nearest the onset first. The first beat's band reaches back to
// Set (see onsetBandsMs), so a keen first tap from Set is drawn too.
function tapsInBand(beat: BeatResult, band: OnsetBand): number[] {
  return beat.tapsMs
    .filter((tapMs) => isInBand(tapMs, band))
    .sort((lhs, rhs) => Math.abs(lhs - beat.onsetMs) - Math.abs(rhs - beat.onsetMs));
}

function beatTapMarks(beat: BeatResult, inBand: number[], value: NoteValue, msPerBeat: number): TapMark[] {
  const matched = inBand[0]; // undefined when nothing landed in the band
  const color = resultColorForStatus(beat.status); // red when extra taps made the beat a miss
  return beat.tapsMs.map((tapMs) => {
    const isMatched = tapMs === matched;
    return {
      beats: clampBeats(tapMs / msPerBeat),
      tapMs,
      value: isMatched ? value : 'quarter',
      color: isMatched ? color : 'red',
      kind: isMatched ? 'matched' : 'extra',
    };
  });
}

// A tap before the bar start (inside the first band, which reaches back to Set) draws at the bar
// start; one in the trailing band past the bar's end draws at the end
function clampBeats(beats: number): number {
  return Math.min(Math.max(beats, 0), rhythmBeatsPerBar);
}
